import { useState } from "react";
import { Row } from "react-bootstrap";
import { useStateMachine } from "little-state-machine";
import { useQueryClient } from "@tanstack/react-query";
import { useParams, useLocation } from "react-router-dom";
import { FormattedMessage, useIntl } from "react-intl";
import Cookies from "js-cookie";

import { shipmentQueryKeys } from "api/queries/shipments/shipmentQueryKeys";
import { useGetShipmentBasic } from "api/queries/shipments/useGetShipmentBasic";
import { useGetQuickRequestDetails } from "api/queries/quick-request/useGetQuickRequestDetails";
import { useGetShipmentDetails } from "api/queries/shipments/useGetShipmentDetails";
import { useGetQuoteRequestDetails } from "api/queries/quotes/useGetQuoteRequestDetails";
import useCustomBrokerPermission from "hooks/useCustomBrokerPermission";
import usePermissions from "hooks/usePermissions";
import useUserNavigationHistory from "hooks/useUserNavigationHistory";
import AlertV2 from "components/v2/Alert/Alert";
import Column from "components/atoms/Column/Column";
import NavPills from "components/molecules/TopNav/common/NavPills";
import { ShipmentTypes } from "constants/";
import testIds from "constants/testIds";
import TopNavQuickQuote from "./QuickQuotePage/TopNavQuickQuote";
import TopNavBackButton from "./TopNavBackButton";
import ShipmentIdentifier from "./ShipmentIdentifier";
import ActionButtons from "./TopNavActions/ActionButtons";
import ActionIconButtons from "./TopNavActions/ActionIconButtons";
import TopNavTrackingPage from "./TrackingPage";
import TopNavBillingPage from "./BillingPage";
import TopNavBadges from "./TopNavBadges";
import "./TopNavV2.scss";

const SHIPMENT_STATUS = ["Delayed", "In transit", "Incomplete", "Delivered", "Auto-closed", "Booked", "Cancelled"];

interface TopNavV2Props {
  isQuickRequest?: boolean;
  tabTitle: string;
  isLoading?: boolean;
  isProviderQuotationPage?: boolean;
  isProviderTrackingPage?: boolean;
  isConsolidated?: boolean;
}

function TopNavV2({
  isQuickRequest = false,
  tabTitle,
  isLoading = false,
  isProviderQuotationPage = false,
  isProviderTrackingPage = false,
  isConsolidated = false,
}: TopNavV2Props) {
  const intl = useIntl();
  const { permissions } = usePermissions();
  const { user_type } = JSON.parse(permissions || "{}");
  const [isCustomBroker] = useCustomBrokerPermission(isQuickRequest);
  const { state } = useStateMachine() as any;
  const { id } = useParams();
  const shipmentId = (id || state.shipmentId) as string;
  const queryClient = useQueryClient();
  const location = useLocation();
  const [showSuccessPopup, setShowSuccessPopup] = useState((location.state as any)?.showSuccessPopup || false);

  const billingTabTitle = intl.formatMessage({ id: "quote.billingsTabTitle", defaultMessage: "Billings" });

  // Data fetching
  const { data: shipmentData } = useGetShipmentBasic(shipmentId, {
    enabled: !isQuickRequest && !!shipmentId,
  });
  const { data: quickDetailsData, isInitialLoading } = useGetQuickRequestDetails(shipmentId, {
    enabled: isQuickRequest && !!shipmentId,
    staleTime: 30 * 1000,
  });
  const { data: quoteDetailsData } = useGetQuoteRequestDetails({
    shipmentId,
    options: {
      enabled: !!shipmentId && !isQuickRequest,
      refetchOnMount: false,
      staleTime: 30 * 1000,
    },
  });

  const isShipment = !!shipmentId && SHIPMENT_STATUS.includes(shipmentData?.shipment?.request_status);
  const isParentShipment = shipmentData?.related_shipments?.some((item: any) => item?.relation?.toLowerCase() === "child");
  const { data: shipmentDetails = {} } = useGetShipmentDetails(shipmentId, Cookies.get("is_guest") !== "true", {
    enabled: !!shipmentId && isShipment && !isParentShipment,
  }) as any;

  useUserNavigationHistory(
    shipmentId,
    isQuickRequest ? "Quick Request" : "Shipment",
    window.location.pathname,
    state.shipmentTitle,
  );

  // Derived state
  const parentShipment = shipmentData?.related_shipments?.find((s: any) => s.relation === "Parent");
  const cachedShipments = queryClient.getQueryData(shipmentQueryKeys.allShipments()) as any[];
  const shipment = cachedShipments?.find((s: any) => s?.shipment_reference === shipmentId);

  const shipmentTitle = isQuickRequest
    ? (quickDetailsData as any)?.organization?.quick_request_title
    : shipmentData?.shipment?.title || shipment?.shipment_title || (shipmentDetails as any)?.organization?.title;
  const shipmentReference = shipmentId || shipment?.shipment_reference;
  const isDataLoading = (isLoading && !shipment) || (isQuickRequest && isInitialLoading);

  const isMultiStop = shipmentData?.shipment?.shipment_type === ShipmentTypes.MULTISTOP;
  const isMultiDrop = shipmentData?.shipment?.shipment_type === ShipmentTypes.MULTIDROP;
  const numberOfStops = quoteDetailsData?.schedule?.stops?.length || 0;

  const isGuestUser = Cookies.get("is_guest") === "true";
  const isViewingAsGuest = shipmentDetails?.viewing_as_guest_user;
  const isGuest = isGuestUser || isViewingAsGuest;

  const numberOfQuotes = shipmentData?.shipment?.quotes_received;
  const isOffPlatform = shipmentData?.shipment?.is_off_platform_shipment || false;

  const isQuotesPage = location.pathname === `/shipment/${shipmentId}`;
  const isQuickRequestPage = location.pathname === `/shipment/${shipmentId}/quick`;
  const showQuickQuoteDetailsHeader = isQuotesPage || isQuickRequestPage;
  const detailsData = isQuickRequest ? quickDetailsData : quoteDetailsData;

  const isTrackingsPage = location.pathname === `/shipment/${shipmentId}/tracking`;
  const isBillingPage = location.pathname === `/shipment/${shipmentId}/billing`;

  return (
    <div data-testid={testIds.TOPNAV_V2_ROOT}>
      {showSuccessPopup && (
        <div className="pb-4" data-testid={testIds.TOPNAV_V2_SUCCESS_ALERT_CONTAINER}>
          <AlertV2
            variant="success"
            label={intl.formatMessage(
              {
                id: "quote-request.createdSuccessfullyAlert",
                defaultMessage: "Your quote request has successfully been created",
              },
              { shipmentId },
            )}
            onClose={() => setShowSuccessPopup(false)}
          >
            <FormattedMessage
              id="quote-request.createdSuccessfullyAlertMessage"
              defaultMessage="Select a quote below to book your shipment."
            />
          </AlertV2>
        </div>
      )}

      <Column>
        <Row>
          <div className="topnav-v2 w-100" data-testid={testIds.TOPNAV_V2_HEADER}>
            <div className="topnav-v2__grid">
              {/* Back button */}
              <div className="topnav-v2__back-container" data-testid={testIds.TOPNAV_V2_TOP_ROW}>
                <div className="d-flex align-items-center">
                  <TopNavBackButton />
                  <div className="topnav-v2__divider" />
                </div>
              </div>

              {/* Shipment identifier (ID + parent + bookmark) */}
              <div className="topnav-v2__identifier-container">
                <ShipmentIdentifier
                  isDataLoading={isDataLoading}
                  shipmentReference={shipmentReference}
                  isQuickRequest={isQuickRequest}
                />
              </div>

              {/* CTA action buttons (e.g. Assign a team, Pickup, Invoice) */}
              <div className="topnav-v2__cta-buttons-container">
                <ActionButtons
                  isQuickRequest={isQuickRequest}
                  tabTitle={tabTitle}
                  isLoading={isLoading}
                  isProviderQuotationPage={isProviderQuotationPage}
                  isProviderTrackingPage={isProviderTrackingPage}
                  hasParentShipment={!!parentShipment?.shipment_id}
                  isGuest={isGuest}
                />
              </div>

              {/* Icon action buttons (edit, export, menu dots) */}
              <div className="topnav-v2__icon-buttons-container">
                <ActionIconButtons
                  isQuickRequest={isQuickRequest}
                  shipmentId={shipmentReference}
                  tabTitle={tabTitle}
                  isLoading={isLoading}
                  isConsolidated={isConsolidated}
                  isGuest={isGuest}
                />
              </div>

              {/* Shipment title */}
              {shipmentTitle && (
                <div className="topnav-v2__title" data-testid={testIds.TOPNAV_V2_TITLE}>
                  {shipmentTitle}
                </div>
              )}

              {/* Badges */}
              <div className="topnav-v2__badges-container">
                <TopNavBadges
                  isMultiStop={isMultiStop}
                  isMultiDrop={isMultiDrop}
                  numberOfStops={numberOfStops}
                  shipmentReference={shipmentReference}
                  isCustomBroker={isCustomBroker}
                  showBrokerBadge={tabTitle !== billingTabTitle}
                  userType={user_type}
                  isOffPlatform={isOffPlatform}
                />
              </div>
            </div>

            {/* Horizontal underline tabs */}
            <div className="topnav-v2__tabs hide-during-print" data-testid={testIds.TOPNAV_V2_TABS_CONTAINER}>
              <NavPills isLoading={isLoading} isQuickRequest={isQuickRequest} numberOfQuotes={numberOfQuotes} />
            </div>

            {/* Details row for quick/quote, tracking, and billing pages */}
            {showQuickQuoteDetailsHeader && <TopNavQuickQuote data={detailsData} isQuickRequest={isQuickRequest} />}
            {isTrackingsPage && <TopNavTrackingPage />}
            {isBillingPage && <TopNavBillingPage />}
          </div>
        </Row>
      </Column>
    </div>
  );
}

export default TopNavV2;

TopNavV2.defaultProps = {
  isQuickRequest: false,
  isLoading: false,
  isProviderQuotationPage: false,
  isProviderTrackingPage: false,
  isConsolidated: false,
};
